import { useState, useEffect } from 'react';
import { getAll, add, update, remove, STORES } from '../db';
import { Search, Plus, ChevronDown, ChevronUp, Trash2, Edit3, Save, X, Filter } from 'lucide-react';

const CATEGORIES = ['Cleanse', 'Treat — Acne', 'Treat — Pigmentation', 'Treat — Rosacea', 'Treat — Hydrate', 'Treat — Eyes & Lips', 'Treat — Aftercare', 'Protect', 'Prescription', 'Future / Considering'];
const PRODUCT_TYPES = ['Skincare', 'Injectable', 'Consumable', 'Equipment', 'Prescription'];

const emptyProduct = {
  name: '', brand: '', supplier: '', category: '', type: 'Skincare',
  size: '', rrp: '', tradeExVat: '', tradeIncVat: '',
  description: '', howToUse: '', whenToUse: '', ingredients: '',
  qtyPerPack: '', qtyPerTreatment: '', costPerUse: '',
  inStock: true, stockQty: '', expiryDate: '', reorderPoint: '',
  notes: '', status: 'Active',
};

export default function Products() {
  const [products, setProducts] = useState([]);
  const [suppliers, setSuppliers] = useState([]);
  const [search, setSearch] = useState('');
  const [catFilter, setCatFilter] = useState('');
  const [expanded, setExpanded] = useState(null);
  const [editing, setEditing] = useState(null);
  const [form, setForm] = useState(emptyProduct);
  const [showNew, setShowNew] = useState(false);
  const [activeTab, setActiveTab] = useState('details');

  useEffect(() => { load(); }, []);

  const load = async () => {
    const [prods, sups] = await Promise.all([getAll(STORES.products), getAll(STORES.suppliers)]);
    setProducts(prods.sort((a, b) => a.name?.localeCompare(b.name)));
    setSuppliers(sups);
  };

  const filtered = products.filter(p => {
    const matchSearch = `${p.name} ${p.brand} ${p.supplier} ${p.category}`.toLowerCase().includes(search.toLowerCase());
    const matchCat = !catFilter || p.category === catFilter;
    return matchSearch && matchCat;
  });

  const margin = (p) => {
    if (!p.rrp || !p.tradeIncVat) return null;
    return Math.round(((parseFloat(p.rrp) - parseFloat(p.tradeIncVat)) / parseFloat(p.rrp)) * 100);
  };

  const saveNew = async () => {
    if (!form.name) return;
    await add(STORES.products, form);
    setForm(emptyProduct);
    setShowNew(false);
    load();
  };

  const saveEdit = async () => {
    await update(STORES.products, editing);
    setEditing(null);
    load();
  };

  const deleteProduct = async (id) => {
    if (!window.confirm('Delete this product?')) return;
    await remove(STORES.products, id);
    setExpanded(null);
    load();
  };

  const ProductForm = ({ data, setData, onSave, onCancel }) => (
    <div className="patient-form">
      <div className="form-section-title">Product Details</div>
      <div className="form-grid">
        <div className="form-field full"><label>Product Name *</label><input value={data.name} onChange={e => setData({ ...data, name: e.target.value })} /></div>
        <div className="form-field"><label>Brand</label><input value={data.brand} onChange={e => setData({ ...data, brand: e.target.value })} /></div>
        <div className="form-field"><label>Supplier</label>
          <select value={data.supplier} onChange={e => setData({ ...data, supplier: e.target.value })}>
            <option value="">Select supplier...</option>
            {suppliers.map(s => <option key={s.id} value={s.name}>{s.name}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Category</label>
          <select value={data.category} onChange={e => setData({ ...data, category: e.target.value })}>
            <option value="">Select category...</option>
            {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Type</label>
          <select value={data.type} onChange={e => setData({ ...data, type: e.target.value })}>
            {PRODUCT_TYPES.map(t => <option key={t} value={t}>{t}</option>)}
          </select>
        </div>
        <div className="form-field"><label>Size / Pack</label><input value={data.size} onChange={e => setData({ ...data, size: e.target.value })} placeholder="e.g. 50ml, 5 x 2ml" /></div>
        <div className="form-field"><label>Status</label>
          <select value={data.status} onChange={e => setData({ ...data, status: e.target.value })}>
            <option value="Active">Active</option>
            <option value="Future / Considering">Future / Considering</option>
            <option value="Discontinued">Discontinued</option>
          </select>
        </div>
      </div>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Pricing</div>
      <div className="form-grid">
        <div className="form-field"><label>RRP (inc VAT) £</label><input type="number" step="0.01" value={data.rrp} onChange={e => setData({ ...data, rrp: e.target.value })} /></div>
        <div className="form-field"><label>Trade Price (ex VAT) £</label><input type="number" step="0.01" value={data.tradeExVat} onChange={e => setData({ ...data, tradeExVat: e.target.value })} /></div>
        <div className="form-field"><label>Trade Price (inc VAT) £</label><input type="number" step="0.01" value={data.tradeIncVat} onChange={e => setData({ ...data, tradeIncVat: e.target.value })} /></div>
      </div>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Stock</div>
      <div className="form-grid">
        <div className="form-field"><label>Qty in Stock</label><input type="number" value={data.stockQty} onChange={e => setData({ ...data, stockQty: e.target.value })} /></div>
        <div className="form-field"><label>Reorder Point</label><input type="number" value={data.reorderPoint} onChange={e => setData({ ...data, reorderPoint: e.target.value })} /></div>
        <div className="form-field"><label>Expiry Date</label><input type="date" value={data.expiryDate} onChange={e => setData({ ...data, expiryDate: e.target.value })} /></div>
        <div className="form-field"><label>Qty Per Pack</label><input type="number" value={data.qtyPerPack} onChange={e => setData({ ...data, qtyPerPack: e.target.value })} /></div>
        <div className="form-field"><label>Qty Used Per Treatment</label><input type="number" step="0.1" value={data.qtyPerTreatment} onChange={e => setData({ ...data, qtyPerTreatment: e.target.value })} /></div>
      </div>

      <div className="form-section-title" style={{ marginTop: '1rem' }}>Description & Usage</div>
      <div className="form-grid">
        <div className="form-field full"><label>Description (for prescription)</label><textarea value={data.description} onChange={e => setData({ ...data, description: e.target.value })} rows={3} placeholder="What does this product do?" /></div>
        <div className="form-field full"><label>How to Use</label><textarea value={data.howToUse} onChange={e => setData({ ...data, howToUse: e.target.value })} rows={2} placeholder="Application instructions..." /></div>
        <div className="form-field full"><label>When to Use</label><textarea value={data.whenToUse} onChange={e => setData({ ...data, whenToUse: e.target.value })} rows={2} placeholder="AM / PM / frequency..." /></div>
        <div className="form-field full"><label>Key Ingredients</label><textarea value={data.ingredients} onChange={e => setData({ ...data, ingredients: e.target.value })} rows={2} /></div>
        <div className="form-field full"><label>Notes</label><textarea value={data.notes} onChange={e => setData({ ...data, notes: e.target.value })} rows={2} /></div>
      </div>

      <div className="form-actions">
        <button className="btn-ghost" onClick={onCancel}><X size={14} /> Cancel</button>
        <button className="btn-gold" onClick={onSave}><Save size={14} /> Save Product</button>
      </div>
    </div>
  );

  const groupedByCategory = CATEGORIES.reduce((acc, cat) => {
    const items = filtered.filter(p => p.category === cat);
    if (items.length) acc[cat] = items;
    return acc;
  }, {});
  const uncategorised = filtered.filter(p => !p.category);

  return (
    <div className="section-content">
      <div className="section-header-row">
        <div>
          <h2 className="section-title">Products</h2>
          <p className="section-sub">{products.length} product{products.length !== 1 ? 's' : ''}</p>
        </div>
        <button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add Product</button>
      </div>

      <div className="search-bar">
        <Search size={15} className="search-icon" />
        <input value={search} onChange={e => setSearch(e.target.value)} placeholder="Search products, brands..." className="search-input" />
        <select value={catFilter} onChange={e => setCatFilter(e.target.value)} className="filter-select">
          <option value="">All categories</option>
          {CATEGORIES.map(c => <option key={c} value={c}>{c}</option>)}
        </select>
      </div>

      {showNew && (
        <div className="patient-card expanded" style={{ marginBottom: '1rem' }}>
          <div className="card-header"><div className="patient-name-block"><div className="patient-initials">+</div><div className="patient-name">New Product</div></div></div>
          <ProductForm data={form} setData={setForm} onSave={saveNew} onCancel={() => { setShowNew(false); setForm(emptyProduct); }} />
        </div>
      )}

      {Object.entries(groupedByCategory).map(([cat, items]) => (
        <div key={cat} className="product-category-group">
          <div className="category-group-header">{cat} <span className="count-badge">{items.length}</span></div>
          {items.map(p => {
            const m = margin(p);
            return (
              <div key={p.id} className={`patient-card ${expanded === p.id ? 'expanded' : ''}`}>
                <div className="card-header" onClick={() => { setExpanded(expanded === p.id ? null : p.id); setEditing(null); setActiveTab('details'); }}>
                  <div className="patient-name-block">
                    <div className="patient-initials" style={{ fontSize: '10px' }}>{p.brand?.[0] || p.name?.[0]}</div>
                    <div>
                      <div className="patient-name">
                        {p.name}
                        {p.status === 'Future / Considering' && <span className="status-badge future">Future</span>}
                        {p.status === 'Discontinued' && <span className="status-badge discontinued">Discontinued</span>}
                      </div>
                      <div className="patient-meta">
                        {p.brand && <span>{p.brand}</span>}
                        {p.size && <span>{p.size}</span>}
                        {p.rrp && <span>RRP £{parseFloat(p.rrp).toFixed(2)}</span>}
                        {p.tradeIncVat && <span>Trade £{parseFloat(p.tradeIncVat).toFixed(2)}</span>}
                        {m !== null && <span className={`margin-badge ${m > 50 ? 'good' : m > 30 ? 'ok' : 'low'}`}>{m}% margin</span>}
                      </div>
                    </div>
                  </div>
                  <div className="card-actions" onClick={e => e.stopPropagation()}>
                    <button className="icon-btn" onClick={() => { setEditing(p); setExpanded(p.id); }}><Edit3 size={14} /></button>
                    <button className="icon-btn danger" onClick={() => deleteProduct(p.id)}><Trash2 size={14} /></button>
                    <span className="expand-icon">{expanded === p.id ? <ChevronUp size={16} /> : <ChevronDown size={16} />}</span>
                  </div>
                </div>

                {expanded === p.id && (
                  <div className="card-body">
                    {editing?.id === p.id ? (
                      <ProductForm data={editing} setData={setEditing} onSave={saveEdit} onCancel={() => setEditing(null)} />
                    ) : (
                      <>
                        <div className="patient-tabs">
                          {['details', 'pricing', 'usage', 'stock'].map(tab => (
                            <button key={tab} className={`patient-tab ${activeTab === tab ? 'active' : ''}`} onClick={() => setActiveTab(tab)}>
                              {tab.charAt(0).toUpperCase() + tab.slice(1)}
                            </button>
                          ))}
                        </div>
                        {activeTab === 'details' && (
                          <div className="detail-grid">
                            <div className="detail-row"><span className="detail-label">Brand</span><span>{p.brand || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Supplier</span><span>{p.supplier || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Size</span><span>{p.size || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Type</span><span>{p.type || '—'}</span></div>
                            <div className="detail-row full"><span className="detail-label">Description</span><span>{p.description || '—'}</span></div>
                            <div className="detail-row full"><span className="detail-label">Key Ingredients</span><span>{p.ingredients || '—'}</span></div>
                          </div>
                        )}
                        {activeTab === 'pricing' && (
                          <div className="detail-grid">
                            <div className="detail-row"><span className="detail-label">RRP (inc VAT)</span><span>{p.rrp ? `£${parseFloat(p.rrp).toFixed(2)}` : '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Trade (ex VAT)</span><span>{p.tradeExVat ? `£${parseFloat(p.tradeExVat).toFixed(2)}` : '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Trade (inc VAT)</span><span>{p.tradeIncVat ? `£${parseFloat(p.tradeIncVat).toFixed(2)}` : '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Gross Margin</span><span className={m !== null ? (m > 50 ? 'text-good' : m > 30 ? 'text-ok' : 'text-low') : ''}>{m !== null ? `${m}%` : '—'}</span></div>
                          </div>
                        )}
                        {activeTab === 'usage' && (
                          <div className="detail-grid">
                            <div className="detail-row full"><span className="detail-label">How to Use</span><span>{p.howToUse || '—'}</span></div>
                            <div className="detail-row full"><span className="detail-label">When to Use</span><span>{p.whenToUse || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Qty Per Pack</span><span>{p.qtyPerPack || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Qty Per Treatment</span><span>{p.qtyPerTreatment || '—'}</span></div>
                          </div>
                        )}
                        {activeTab === 'stock' && (
                          <div className="detail-grid">
                            <div className="detail-row"><span className="detail-label">In Stock</span><span>{p.stockQty || '0'} units</span></div>
                            <div className="detail-row"><span className="detail-label">Reorder Point</span><span>{p.reorderPoint || '—'}</span></div>
                            <div className="detail-row"><span className="detail-label">Expiry Date</span><span>{p.expiryDate ? new Date(p.expiryDate).toLocaleDateString('en-GB') : '—'}</span></div>
                          </div>
                        )}
                      </>
                    )}
                  </div>
                )}
              </div>
            );
          })}
        </div>
      ))}

      {uncategorised.length > 0 && (
        <div className="product-category-group">
          <div className="category-group-header">Uncategorised <span className="count-badge">{uncategorised.length}</span></div>
          {uncategorised.map(p => (
            <div key={p.id} className="patient-card">
              <div className="card-header">
                <div className="patient-name-block">
                  <div className="patient-initials">{p.name?.[0]}</div>
                  <div className="patient-name">{p.name}</div>
                </div>
                <div className="card-actions">
                  <button className="icon-btn" onClick={() => { setEditing(p); setExpanded(p.id); }}><Edit3 size={14} /></button>
                  <button className="icon-btn danger" onClick={() => deleteProduct(p.id)}><Trash2 size={14} /></button>
                </div>
              </div>
            </div>
          ))}
        </div>
      )}

      {filtered.length === 0 && !showNew && (
        <div className="empty-state"><p>No products found</p><button className="btn-gold" onClick={() => setShowNew(true)}><Plus size={14} /> Add your first product</button></div>
      )}
    </div>
  );
}
